import { pageIntroContent } from '@insify/css/classes.module.css';
import { Panel, PanelAnchor } from '@insify/design-system/components';
import {
  Alarm,
  BabyWagon,
  Boxes,
  Bridge,
  Calculator,
  Calendar,
  CalendarCheck,
  Chat,
  Collaborate,
  Cosmetic,
  CyberCrime,
  Document,
  DocumentSearch,
  Drugs,
  Earth,
  Education,
  ElectricGoods,
  Employees,
  Fire,
  Flight,
  GasPipe,
  Graduation,
  Handshake,
  Heart,
  Hospital,
  House,
  Indexation,
  Inspection,
  Kids,
  Legal,
  License,
  LoadBearing,
  Location,
  Manufacturer,
  Medal,
  OfficeBuilding,
  Ownership,
  RepairTools,
  Robotics,
  Roofing,
  Sanitary,
  ShieldCheck,
  Shop,
  Snowflake,
  TagAdd,
  Toxic,
} from '@insify/design-system/line-icons';
import { ContentContextWrapper } from '@objectiv/tracker-react';
import { useForm } from 'react-hook-form';

import { RichContentWithTooltip } from '../../components/RichContent';
import { WizardLayoutFormWithCommonSection } from '../../components/WizardLayoutForm';

import * as styles from './ActivitySpecificQuestions.module.css';

import type { RichContentType } from '../../components/RichContent';
import type { ActiveActivitySpecificField, ActiveActivitySpecificFields } from '@insify/shared-company-policy-model';
import type React from 'react';
import type { Control, DeepPartial, UseFormRegister, UseFormSetValue } from 'react-hook-form';

const defaultIcon = <Document />;

export const activityQuestionIconMapping: Record<ActiveActivitySpecificField, React.ReactNode> = {
  faultyCraftmanship: defaultIcon,
  jointRestoration: defaultIcon,
  mboDegree: defaultIcon,
  medicallyOverqualified: defaultIcon,
  minThreeYearsExperience: defaultIcon,
  productCertification: defaultIcon,
  sunProtectionInstallation: defaultIcon,
  thirdPartyObjectsSites: defaultIcon,
  thresholdSubcontractors: defaultIcon,
  tradeElectricBlankets: defaultIcon,
  tradeFur: defaultIcon,
  academicDegree: <Graduation />,
  cosmeticInvasiveProcedures: <Cosmetic />,
  fireHazardWork: <Fire />,
  gasFitting: <GasPipe />,
  importOutsideEu: <Flight />,
  loadBearing: <LoadBearing />,
  meisterTitle: <Graduation />,
  ownBrand: <TagAdd />,
  planningServices: <Calendar />,
  repairProducts: <RepairTools />,
  roofing: <Roofing />,
  sanitaryInstallation: <Sanitary />,
  stationaryStore: <Shop />,
  tradeElectronics: <ElectricGoods />,
  workOnCustomerSite: <Location />,
  capDegree: <Graduation />,
  kidsUnderThree: <BabyWagon />,
  kidsNumber: <Kids />,
  decennaleRequired: <ShieldCheck />,
  workingOnlyAtInstitutions: <OfficeBuilding />,
  thresholdSubcontractors20: <Employees />,
  thresholdSubcontractors40: <Employees />,
  regulatedProfession: <Medal />,
  relevantDegree: <Graduation />,
  highestRelevantDegree: <Graduation />,
  tradeTobaccoLotteryParcel: <Shop />,
  medicalCleaning: <Hospital />,
  winterCleaningMaintenance: <Snowflake />,
  roofFacadeCleaning: <Roofing />,
  constructionCompletionCleaning: <OfficeBuilding />,
  highestDutchDegree: <Graduation />,
  relevantWorkExperience: <Ownership />,
  doesMergersAndAcquisitions: <Medal />,
  givesPersonalInjuryAdvice: <Heart />,
  worksForListedCompanies: <Indexation />,
  givesForeignLawAdvice: <Earth />,
  possessionPersonalDetails50000: <CyberCrime />,
  possessionPersonalDetails25000: <CyberCrime />,
  relevantYearsWorkExperience2: <CalendarCheck />,
  relevantYearsWorkExperience3: <CalendarCheck />,
  certifiedEstateAgentOrSVM: <Graduation />,
  legalOrAdministrativeCourse: <Education />,
  shareAssuranceRevenue: <DocumentSearch />,
  shareAssuranceRevenue10: <DocumentSearch />,
  worksForPublicOrganisations: <Document />,
  needsAfmPermit: defaultIcon, // waiting for svg from the design team
  shareAutomationRevenue: <Education />,
  pastFailureToInsurePI: <Alarm />,
  worksOnForeignLawOrTaxes: <Earth />,
  subsidiesWork: <Chat />,
  foreignLawAdvice: <Legal />,
  doesSettlements: <Calculator />,
  membershipLegalCostPartnership: <Collaborate />,
  registersIntellectualPropertyRights: <License />,
  estimatesBuildingCosts: <Calculator />,
  calculationsForRiskyInfrastructure: <Bridge />,
  designsMassProducedProducts: <Boxes />,
  worksOnAutomation: <Robotics />, // waiting for svg from the design team
  environmentalAdvice: <Toxic />,
  worksInRiskySectors: <Manufacturer />,
  doesConstructionProjectDevelopment: <RepairTools />,
  doesDesignAndBuild: <Inspection />,
  limitsLiabilityWithCorrectDeliveryTerms: defaultIcon, // waiting for svg from the design team
  worksInHospitals: <Hospital />,
  sellsHealthGoods: <Drugs />,
  nvrMembership: <Handshake />,
  worksOnPropertyRightsOutsideRealEstate: <Chat />,
  managesVves: <Employees />,
  shareMaintenanceOfPropertyRevenue: <House />,
  shareCorporatePropertyRevenue: <OfficeBuilding />,
  shareAgriculturalPropertyRevenue: defaultIcon, // waiting for svg from the design team
  hasWftPermit: <License />,
  failedToMeetWftRequirements: <Alarm />,
  maintenanceAndRepair: <RepairTools />,
};

export type ActivitySpecificQuestionsFormType = {
  activitySpecificFieldAnswers: ActiveActivitySpecificFields;
};

export type ActivitySpecificFieldDescription = {
  key: ActiveActivitySpecificField;
  text: string;
  detailedText?: string;
  component?: (props: {
    register: UseFormRegister<ActivitySpecificQuestionsFormType>;
    control: Control<ActivitySpecificQuestionsFormType>;
    setValue: UseFormSetValue<ActivitySpecificQuestionsFormType>;
  }) => JSX.Element;
};

export type ActivitySpecificQuestionsProps = {
  formData: DeepPartial<ActivitySpecificQuestionsFormType>;
  questions: ActivitySpecificFieldDescription[];
  eventHandlers: {
    onSubmit: (values: ActivitySpecificQuestionsFormType) => void;
    onBack: () => void;
  };
  content?: RichContentType;
  translations: {
    back: string;
    forward: string;
    readMore: string;
  };
  submitting: boolean;
};

export const ActivitySpecificQuestions = ({
  formData,
  questions,
  eventHandlers,
  content,
  translations,
  submitting,
}: ActivitySpecificQuestionsProps): JSX.Element => {
  const { register, handleSubmit, control, setValue } = useForm<ActivitySpecificQuestionsFormType>({
    shouldUnregister: true,
    defaultValues: formData,
    mode: 'onSubmit',
  });

  return (
    <WizardLayoutFormWithCommonSection
      backCopy={translations.back}
      onBack={eventHandlers.onBack}
      forwardCopy={translations.forward}
      isSubmitting={submitting}
      onSubmit={handleSubmit(eventHandlers.onSubmit)}
    >
      <RichContentWithTooltip content={content} className={pageIntroContent} />
      {questions.map(({ key, text, detailedText, component }) => {
        if (component !== undefined) {
          return component({ register, control, setValue });
        }

        const description = (
          <>
            <span dangerouslySetInnerHTML={{ __html: text }} />
            {detailedText && detailedText.length > 0 && (
              <>
                {' '}
                <PanelAnchor data-testid={`readMore-${key}`}>{translations.readMore}</PanelAnchor>
              </>
            )}
          </>
        );

        return (
          <ContentContextWrapper key={key} id={key}>
            <Panel
              className={styles.panel}
              id={`activitySpecificFieldAnswers.${key}`}
              icon={activityQuestionIconMapping[key]}
              trackingId='toggle'
              aria-label={text}
              disabled={submitting}
              {...register(`activitySpecificFieldAnswers.${key}`)}
              description={description}
              details={detailedText && <span dangerouslySetInnerHTML={{ __html: detailedText }}></span>}
            />
          </ContentContextWrapper>
        );
      })}
    </WizardLayoutFormWithCommonSection>
  );
};
